/**
 * PatientCheckInForm - Form component for patient check-in
 * 
 * Displays:
 * - Welcome message from template
 * - Pre-populated patient data fields (editable)
 * - Custom fields for patient to fill
 * - Review and submit
 * 
 * Supports editing patient CMR data with history tracking for:
 * - Name changes (added to akaNames)
 * - DOB changes (added to previousDOBs)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore,
  Edit,
  Check,
  Person,
  MedicalServices,
  History,
  WarningAmber,
  HealthAndSafety,
  Send,
  Info,
} from '@mui/icons-material';
import PatientDataEditModal from './PatientDataEditModal';

/**
 * Get nested value from object using dot notation path
 */
const getNestedValue = (obj, path) => {
  if (!obj || !path) return null;
  
  const keys = path.split('.');
  let value = obj;
  
  for (const key of keys) {
    if (value === null || value === undefined) return null;
    value = value[key];
  }
  
  return value;
};

/**
 * Format value for display based on type
 */
const formatDisplayValue = (value, type) => {
  if (value === null || value === undefined) return '—';
  
  if (type === 'array') {
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(', ') : 'None';
    }
    return String(value);
  }
  
  if (type === 'date') {
    if (value instanceof Date) {
      return value.toLocaleDateString();
    }
    if (typeof value === 'string') {
      return new Date(value).toLocaleDateString();
    }
    return String(value);
  }
  
  if (type === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  
  return String(value);
};

/**
 * Get icon for field category
 */
const getCategoryIcon = (categoryId) => {
  const icons = {
    patient: <Person />,
    medications: <MedicalServices />,
    pmh: <History />,
    allergies: <WarningAmber />,
    insurance: <HealthAndSafety />,
  };
  return icons[categoryId] || <Person />;
};

/**
 * Patient data field display (editable with history tracking)
 */
const PatientDataField = ({ field, value, onEdit, isModified, isEditable = true }) => {
  const displayValue = formatDisplayValue(value, field.dataType);
  
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        py: 1.5,
        px: 2,
        bgcolor: isModified ? 'primary.50' : 'grey.50',
        borderRadius: 1,
        mb: 1,
        border: isModified ? '1px solid' : 'none',
        borderColor: 'primary.main',
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            {field.label}
          </Typography>
          {field.required && (
            <Chip label="Required" size="small" sx={{ height: 16, fontSize: '0.6rem' }} color="error" />
          )}
          {isModified && (
            <Chip label="Modified" size="small" sx={{ height: 16, fontSize: '0.6rem' }} color="primary" />
          )}
        </Box>
        <Typography variant="body1">
          {displayValue}
        </Typography>
      </Box>
      {isEditable && onEdit && (
        <Tooltip title="Edit this information">
          <IconButton size="small" onClick={() => onEdit(field)} color={isModified ? 'primary' : 'default'}>
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

/**
 * Custom field input
 */
const CustomFieldInput = ({ field, value, onChange, error }) => {
  const handleChange = (e) => {
    const newValue = field.fieldType === 'checkbox' ? e.target.checked : e.target.value;
    onChange(field.id, newValue);
  };

  if (field.fieldType === 'dropdown') {
    return (
      <FormControl fullWidth error={!!error} sx={{ mb: 2 }}>
        <InputLabel>{field.label}{field.required && ' *'}</InputLabel>
        <Select
          value={value || ''}
          onChange={handleChange}
          label={`${field.label}${field.required ? ' *' : ''}`}
        >
          {field.options?.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
        {error && <Typography variant="caption" color="error">{error}</Typography>}
      </FormControl>
    );
  }

  if (field.fieldType === 'checkbox') {
    return (
      <FormControlLabel
        control={
          <Checkbox
            checked={!!value}
            onChange={handleChange}
          />
        }
        label={`${field.label}${field.required ? ' *' : ''}`}
        sx={{ mb: 2 }}
      />
    );
  }

  if (field.fieldType === 'textarea') {
    return (
      <TextField
        fullWidth
        multiline
        rows={3}
        label={`${field.label}${field.required ? ' *' : ''}`}
        value={value || ''}
        onChange={handleChange}
        placeholder={field.placeholder}
        error={!!error}
        helperText={error}
        sx={{ mb: 2 }}
      />
    );
  }

  if (field.fieldType === 'date') {
    return (
      <TextField
        fullWidth
        type="date"
        label={`${field.label}${field.required ? ' *' : ''}`}
        value={value || ''}
        onChange={handleChange}
        error={!!error}
        helperText={error}
        InputLabelProps={{ shrink: true }}
        sx={{ mb: 2 }}
      />
    );
  }

  if (field.fieldType === 'number') {
    return (
      <TextField
        fullWidth
        type="number"
        label={`${field.label}${field.required ? ' *' : ''}`}
        value={value || ''}
        onChange={handleChange}
        placeholder={field.placeholder}
        error={!!error}
        helperText={error}
        sx={{ mb: 2 }}
      />
    );
  }

  // Default: text input
  return (
    <TextField
      fullWidth
      label={`${field.label}${field.required ? ' *' : ''}`}
      value={value || ''}
      onChange={handleChange}
      placeholder={field.placeholder}
      error={!!error}
      helperText={error}
      sx={{ mb: 2 }}
    />
  );
};

/**
 * Main PatientCheckInForm component
 */
const PatientCheckInForm = ({ template, userData, onSubmit, akaNames = [], previousDOBs = [], idScanData = null }) => {
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  
  // Editable patient data state
  const [editedPatientData, setEditedPatientData] = useState({});
  const [modifiedFields, setModifiedFields] = useState(new Set());
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);

  // Separate fields by type
  const patientDataFields = template?.fields?.filter(f => f.type === 'patientData') || [];
  const customFields = template?.fields?.filter(f => f.type === 'custom') || [];

  // Group patient data fields by category
  const groupedPatientFields = patientDataFields.reduce((acc, field) => {
    const category = field.dataPath?.split('.')[0] || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(field);
    return acc;
  }, {});

  // Get original patient data value for a field (from userData, overlaid with ID scan data if available)
  const getOriginalPatientDataValue = useCallback((field) => {
    // Parse ID scan name into first/last if available
    const idFirstName = idScanData?.name?.split(' ')[0] || null;
    const idLastName = idScanData?.name?.split(' ').slice(1).join(' ') || null;

    // Map field paths to userData structure, with ID scan data as fallback/override
    const pathMapping = {
      'patient.firstName': userData?.firstName || userData?.name?.split(' ')[0] || idFirstName,
      'patient.lastName': userData?.lastName || userData?.name?.split(' ').slice(1).join(' ') || idLastName,
      'patient.fullName': userData?.name || `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim() || idScanData?.name,
      'patient.dateOfBirth': userData?.dob || userData?.dateOfBirth || idScanData?.dob,
      'patient.age': userData?.age,
      'patient.sex': userData?.sex || userData?.gender,
      'patient.phone': userData?.phone,
      'patient.email': userData?.email,
      'patient.address.street': userData?.address?.street,
      'patient.address.city': userData?.address?.city,
      'patient.address.state': userData?.address?.state,
      'patient.address.zip': userData?.address?.zip,
      'patient.address.full': userData?.address ? 
        `${userData.address.street || ''}, ${userData.address.city || ''}, ${userData.address.state || ''} ${userData.address.zip || ''}`.trim() : null,
      'patient.emergencyContact.name': userData?.emergencyContact?.name,
      'patient.emergencyContact.relationship': userData?.emergencyContact?.relationship,
      'patient.emergencyContact.phone': userData?.emergencyContact?.phone,
      'medications.all': userData?.medications,
      'medications.count': userData?.medications?.length,
      'pmh.all': userData?.pmh || userData?.pastMedicalHistory,
      'pmh.active': userData?.pmh?.filter?.(c => c.active),
      'pmh.count': userData?.pmh?.length,
      'allergies.all': userData?.allergies,
      'allergies.count': userData?.allergies?.length,
      'allergies.hasKnownAllergies': userData?.allergies?.length > 0,
      'insurance.provider': userData?.insurance?.provider,
      'insurance.policyNumber': userData?.insurance?.policyNumber,
      'insurance.groupNumber': userData?.insurance?.groupNumber,
    };

    return pathMapping[field.dataPath] ?? getNestedValue(userData, field.dataPath);
  }, [userData, idScanData]);

  // Get current patient data value (edited or original)
  const getPatientDataValue = useCallback((field) => {
    // Return edited value if exists, otherwise original
    if (editedPatientData.hasOwnProperty(field.dataPath)) {
      return editedPatientData[field.dataPath];
    }
    return getOriginalPatientDataValue(field);
  }, [editedPatientData, getOriginalPatientDataValue]);

  // Check if a field is editable
  const isFieldEditable = (field) => {
    const editablePaths = [
      'patient.firstName',
      'patient.lastName',
      'patient.dateOfBirth',
      'patient.sex',
      'patient.phone',
      'patient.email',
      'patient.address.street',
      'patient.address.city',
      'patient.address.state',
      'patient.address.zip',
      'patient.emergencyContact.name',
      'patient.emergencyContact.relationship',
      'patient.emergencyContact.phone',
    ];
    return editablePaths.includes(field.dataPath);
  };

  // Handle opening edit modal
  const handleEditField = (field) => {
    setEditingField(field);
    setEditModalOpen(true);
  };

  // Handle saving edited field
  const handleSaveEdit = ({ field, newValue, originalValue, valueChanged, category }) => {
    if (valueChanged) {
      setEditedPatientData(prev => ({
        ...prev,
        [field.dataPath]: newValue,
      }));
      setModifiedFields(prev => new Set([...prev, field.dataPath]));
    }
  };

  const handleCustomFieldChange = (fieldId, value) => {
    setCustomFieldValues(prev => ({
      ...prev,
      [fieldId]: value,
    }));
    // Clear error when user starts typing
    if (errors[fieldId]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate required custom fields
    customFields.forEach(field => {
      if (field.required) {
        const value = customFieldValues[field.id];
        if (value === undefined || value === null || value === '') {
          newErrors[field.id] = 'This field is required';
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      // Build CMR snapshot data with edited values
      const cmrData = {
        // Patient data snapshot (uses edited values where applicable)
        patientData: {},
        // Custom field responses
        customFieldResponses: customFieldValues,
        // Track which fields were modified for history tracking
        modifiedFields: Array.from(modifiedFields),
        // Original values for history tracking (only for modified fields)
        originalValues: {},
        // Edited values specifically (for CMR update)
        editedValues: { ...editedPatientData },
      };

      // Capture patient data values (edited or original)
      patientDataFields.forEach(field => {
        const currentValue = getPatientDataValue(field);
        cmrData.patientData[field.dataPath] = currentValue;
        
        // If this field was modified, also capture the original value
        if (modifiedFields.has(field.dataPath)) {
          cmrData.originalValues[field.dataPath] = getOriginalPatientDataValue(field);
        }
      });

      // Add convenience flags for name/DOB changes
      cmrData.nameChanged = modifiedFields.has('patient.firstName') || modifiedFields.has('patient.lastName');
      cmrData.dobChanged = modifiedFields.has('patient.dateOfBirth');
      
      // If name changed, include the original full name for AKA
      if (cmrData.nameChanged) {
        cmrData.previousName = {
          firstName: getOriginalPatientDataValue({ dataPath: 'patient.firstName' }),
          lastName: getOriginalPatientDataValue({ dataPath: 'patient.lastName' }),
        };
      }
      
      // If DOB changed, include the original DOB for history
      if (cmrData.dobChanged) {
        cmrData.previousDOB = getOriginalPatientDataValue({ dataPath: 'patient.dateOfBirth' });
      }

      await onSubmit(cmrData);
    } catch (err) {
      console.error('[PatientCheckInForm] Submit error:', err);
      setSubmitError(err.message || 'Failed to complete check-in. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const categoryLabels = {
    patient: 'Personal Information',
    medications: 'Current Medications',
    pmh: 'Medical History',
    allergies: 'Allergies',
    insurance: 'Insurance Information',
    clinic: 'Clinic Information',
    visit: 'Visit Information',
    familyHistory: 'Family History',
  };

  return (
    <Box>
      {/* Welcome Message */}
      {template?.welcomeMessage && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.50' }}>
          <Typography 
            variant="body1" 
            sx={{ whiteSpace: 'pre-line' }}
          >
            {template.welcomeMessage}
          </Typography>
        </Paper>
      )}

      {/* Patient Data Fields (Editable) */}
      {Object.keys(groupedPatientFields).length > 0 && (
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
            <Typography variant="h6">
              Your Information
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please review and update the information below. Click the edit icon to make changes.
            </Typography>
            {modifiedFields.size > 0 && (
              <Alert severity="info" sx={{ mt: 2 }} icon={<Info />}>
                You have modified {modifiedFields.size} field{modifiedFields.size > 1 ? 's' : ''}. 
                These changes will update your medical record.
              </Alert>
            )}
          </Box>
          
          {/* AKA Names Display */}
          {akaNames?.length > 0 && (
            <Box sx={{ px: 2, py: 1, bgcolor: 'info.50', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <History fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  Also Known As (Previous Names):
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {akaNames.map((aka, index) => (
                  <Chip 
                    key={index} 
                    label={`${aka.firstName} ${aka.lastName}`}
                    size="small"
                    variant="outlined"
                    color="info"
                  />
                ))}
              </Box>
            </Box>
          )}
          
          {Object.entries(groupedPatientFields).map(([category, fields]) => (
            <Accordion key={category} defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getCategoryIcon(category)}
                  <Typography variant="subtitle1" fontWeight={600}>
                    {categoryLabels[category] || category}
                  </Typography>
                  <Chip label={fields.length} size="small" sx={{ height: 20 }} />
                  {fields.some(f => modifiedFields.has(f.dataPath)) && (
                    <Chip label="Modified" size="small" sx={{ height: 20 }} color="primary" />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {fields.map(field => (
                  <PatientDataField
                    key={field.id}
                    field={field}
                    value={getPatientDataValue(field)}
                    onEdit={isFieldEditable(field) ? handleEditField : null}
                    isModified={modifiedFields.has(field.dataPath)}
                    isEditable={isFieldEditable(field)}
                  />
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      )}

      {/* Custom Fields (Editable) */}
      {customFields.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Additional Information
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please complete the following fields.
          </Typography>
          
          {customFields.map(field => (
            <CustomFieldInput
              key={field.id}
              field={field}
              value={customFieldValues[field.id]}
              onChange={handleCustomFieldChange}
              error={errors[field.id]}
            />
          ))}
        </Paper>
      )}

      {/* Submit Error */}
      {submitError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSubmitError(null)}>
          {submitError}
        </Alert>
      )}

      {/* Submit Button */}
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          By checking in, you confirm that the information above is accurate.
          {modifiedFields.size > 0 && ' Your changes will be saved to your medical record.'}
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <Send />}
          sx={{ minWidth: 200 }}
        >
          {submitting ? 'Checking In...' : 'Complete Check-in'}
        </Button>
      </Paper>

      {/* Edit Modal */}
      <PatientDataEditModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditingField(null);
        }}
        field={editingField}
        currentValue={editingField ? getPatientDataValue(editingField) : null}
        originalValue={editingField ? getOriginalPatientDataValue(editingField) : null}
        onSave={handleSaveEdit}
        isFirstEntry={editingField?.dataPath === 'patient.dateOfBirth' && !getOriginalPatientDataValue({ dataPath: 'patient.dateOfBirth' })}
        akaNames={akaNames}
        previousDOBs={previousDOBs}
      />
    </Box>
  );
};

export default PatientCheckInForm;
